import { useEffect, useMemo, useState } from "react";
import EntityManager from "@/components/EntityManager";
import { api } from "@/lib/api";
import { today, fmtDate } from "@/lib/format";
import { printAttendanceRegister, printAttendanceSummary, printBlankAttendanceCard, printStaffMonthCard, printBulkAttendanceCards } from "@/lib/print";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CalendarCheck2, Printer, Grid3X3 } from "lucide-react";

import { BRANCHES as OFFICES } from "@/lib/branches";
const STATUS = ["Present", "Absent", "Half Day", "Leave", "Holiday"];
const tone = (s) =>
  s === "Present" ? "border-emerald-300 bg-emerald-50 text-emerald-700"
  : s === "Absent" ? "border-red-300 bg-red-50 text-red-700"
  : s === "Holiday" ? "border-blue-300 bg-blue-50 text-blue-700"
  : "border-amber-300 bg-amber-50 text-amber-700";

function HolidayNotice({ form, setForm }) {
  const [holidays, setHolidays] = useState([]);
  useEffect(() => {
    api.get("/calendar-events").then((r) => setHolidays((r.data || []).filter((e) => e.type === "Holiday"))).catch(() => {});
  }, []);
  const hit = holidays.find((h) => h.date === form.date && (!h.branch || h.branch === "All" || h.branch === form.branch));
  const hitId = hit ? hit.id : null;
  useEffect(() => {
    if (hitId) setForm((f) => (f.status === "Holiday" ? f : { ...f, status: "Holiday" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hitId]);
  if (!hit) return null;
  return (
    <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800" data-testid="holiday-notice">
      <b>{form.date ? new Date(form.date).toLocaleDateString("en-GB") : ""}</b> is an NTC Calendar holiday — <b>{hit.title}</b>. Status is auto-marked as <b>Holiday</b>.
    </div>
  );
}

function MonthlySummary() {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(today().slice(0, 7));
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (open) api.get("/attendance").then((r) => setRows(r.data || [])).catch(() => {});
  }, [open]);

  const list = useMemo(() => {
    const m = {};
    rows.filter((r) => (r.date || "").startsWith(month)).forEach((r) => {
      const key = `${(r.staff_name || "").trim().toUpperCase()}|${r.branch || "Chennai"}`;
      const a = (m[key] = m[key] || { staff_name: r.staff_name, branch: r.branch || "Chennai", designation: r.designation || "", Present: 0, Absent: 0, "Half Day": 0, Leave: 0, Holiday: 0 });
      a[STATUS.includes(r.status) ? r.status : "Present"] += 1;
      if (!a.designation && r.designation) a.designation = r.designation;
    });
    return Object.values(m)
      .map((a) => ({ ...a, payable: a.Present + a.Holiday + a["Half Day"] * 0.5 }))
      .sort((x, y) => (x.staff_name || "").localeCompare(y.staff_name || ""));
  }, [rows, month]);

  const monthLabel = new Date(`${month}-01`).toLocaleDateString("en-GB", { month: "long", year: "numeric" });

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="attendance-summary-btn">
        <CalendarCheck2 className="mr-2 h-4 w-4" /> Monthly Summary
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[860px]">
          <DialogHeader>
            <DialogTitle className="font-display">Monthly Attendance Summary</DialogTitle>
          </DialogHeader>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Month</div>
              <Input type="month" className="h-9 w-[180px]" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="summary-month-input" />
            </div>
            <Button variant="outline" className="h-9" disabled={!list.length} onClick={() => printAttendanceSummary(monthLabel, list)} data-testid="summary-print-btn">
              <Printer className="mr-2 h-4 w-4" /> Print
            </Button>
            <span className="ml-auto text-xs text-muted-foreground">{list.length} staff</span>
          </div>
          <div className="overflow-hidden rounded-lg border border-border">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  {["Sl", "Staff Name", "Designation", "Branch", "Present", "Half Day", "Absent", "Leave", "Holiday", "Payable Days", "Card"].map((h, i) => (
                    <TableHead key={h} className={`text-[11px] uppercase tracking-[0.12em] text-slate-500 ${i >= 4 && i <= 9 ? "text-right" : ""}`}>{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.length === 0 && (
                  <TableRow><TableCell colSpan={11} className="py-10 text-center text-muted-foreground">No attendance records for {monthLabel}.</TableCell></TableRow>
                )}
                {list.map((a, i) => (
                  <TableRow key={i} data-testid="summary-row">
                    <TableCell className="text-sm">{i + 1}</TableCell>
                    <TableCell className="text-sm font-medium">{a.staff_name}</TableCell>
                    <TableCell className="text-sm">{a.designation || "—"}</TableCell>
                    <TableCell className="text-sm">{a.branch}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums text-emerald-700">{a.Present}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums text-amber-700">{a["Half Day"]}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums text-red-600">{a.Absent}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums text-amber-700">{a.Leave}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums text-blue-700">{a.Holiday}</TableCell>
                    <TableCell className="text-right text-sm font-semibold tabular-nums">{a.payable}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        title={`Print ${a.staff_name}'s ${monthLabel} card`}
                        onClick={() => printStaffMonthCard(a, monthLabel, month, rows.filter((r) => (r.date || "").startsWith(month) && (r.staff_name || "").trim().toUpperCase() === (a.staff_name || "").trim().toUpperCase() && (r.branch || "Chennai") === a.branch))}
                        data-testid="print-month-card-btn"
                      >
                        <Printer className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <p className="text-xs text-muted-foreground">Payable Days = Present + Holiday + (Half Day × 0.5). Use with Staff Salary.</p>
        </DialogContent>
      </Dialog>
    </>
  );
}

function HolidayManager() {
  const [open, setOpen] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [date, setDate] = useState("");
  const [name, setName] = useState("");
  const [branch, setBranch] = useState("All");
  const load = () =>
    api.get("/calendar-events").then((r) => setHolidays((r.data || []).filter((e) => e.type === "Holiday").sort((a, b) => String(a.date).localeCompare(String(b.date))))).catch(() => {});
  useEffect(() => { if (open) load(); }, [open]);
  const add = async () => {
    if (!date || !name.trim()) { toast.error("Enter the date and festival/holiday name"); return; }
    try {
      await api.post("/calendar-events", { date, title: name.trim(), type: "Holiday", branch, time: "", notes: "" });
      toast.success(`${name.trim()} added — it will be marked on attendance cards`);
      setName(""); setDate("");
      load();
    } catch { toast.error("Could not save holiday"); }
  };
  const del = async (h) => {
    if (!window.confirm(`Remove holiday ${h.title} (${fmtDate(h.date)})?`)) return;
    try { await api.delete(`/calendar-events/${h.id}`); load(); } catch { toast.error("Could not delete"); }
  };
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="attendance-holidays-btn">
        <CalendarCheck2 className="mr-2 h-4 w-4" /> Holidays
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Festival / Government Holidays</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground">Add any festival or government holiday (15 August, 26 January, 2 October…). It is saved in the NTC Calendar and marked automatically on blank &amp; bulk attendance cards.</div>
            <div className="flex flex-wrap items-center gap-2">
              <Input type="date" className="w-[150px]" value={date} onChange={(e) => setDate(e.target.value)} data-testid="holiday-date-input" />
              <Input className="w-[200px]" placeholder="Festival / holiday name" value={name} onChange={(e) => setName(e.target.value)} data-testid="holiday-name-input" />
              <select className="h-9 rounded-md border bg-background px-2 text-sm" value={branch} onChange={(e) => setBranch(e.target.value)} data-testid="holiday-branch-select">
                {["All", ...OFFICES].map((b) => <option key={b} value={b}>{b === "All" ? "All Branches" : b}</option>)}
              </select>
              <Button size="sm" onClick={add} data-testid="holiday-add-btn">Add</Button>
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  try {
                    const res = await api.post("/calendar-events/preload-national-holidays");
                    toast.success(`National holidays loaded: ${res.data.created} added${res.data.skipped ? `, ${res.data.skipped} already present` : ""}`);
                    load();
                  } catch { toast.error("Could not preload"); }
                }}
                data-testid="holiday-preload-national-btn"
              >
                Load 26 Jan · 1 May · 15 Aug · 2 Oct
              </Button>
            </div>
            <div className="max-h-64 overflow-y-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    {["Date", "Festival / Holiday", "Branch", ""].map((h, i) => <TableHead key={i} className="text-xs">{h}</TableHead>)}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {holidays.map((h) => (
                    <TableRow key={h.id} data-testid="holiday-row">
                      <TableCell className="text-sm">{fmtDate(h.date)}</TableCell>
                      <TableCell className="text-sm font-medium">{h.title}</TableCell>
                      <TableCell className="text-sm">{h.branch || "All"}</TableCell>
                      <TableCell className="text-right">
                        <button type="button" onClick={() => del(h)} className="text-xs font-semibold text-red-600 hover:underline" data-testid="holiday-delete-btn">Remove</button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {holidays.length === 0 && (
                    <TableRow><TableCell colSpan={4} className="py-6 text-center text-sm text-muted-foreground">No holidays yet — add one above or preload in NTC Calendar.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function MonthEntryGrid() {
  const [open, setOpen] = useState(false);
  const [branch, setBranch] = useState(OFFICES[0]);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [staffList, setStaffList] = useState([]);
  const [staffName, setStaffName] = useState("");
  const [existing, setExisting] = useState([]);
  const [grid, setGrid] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.get("/staff-master").then((r) => setStaffList((r.data || []).filter((s) => (s.status || "Active") !== "Inactive"))).catch(() => {});
    api.get("/attendance").then((r) => setExisting(r.data || [])).catch(() => {});
  }, [open]);
  const staff = staffList.filter((s) => !branch || (s.branch || "") === branch);
  const [yy, mm] = month.split("-").map(Number);
  const daysIn = yy && mm ? new Date(yy, mm, 0).getDate() : 0;
  const days = Array.from({ length: daysIn }, (_, i) => i + 1);
  const dateOf = (d) => `${month}-${String(d).padStart(2, "0")}`;
  const existFor = (d) =>
    existing.find((r) => (r.staff_name || "").trim().toUpperCase() === staffName.trim().toUpperCase() && (r.date || "").slice(0, 10) === dateOf(d));
  const cell = (d) => grid[d] || { status: "", in_time: "", out_time: "" };
  const setCell = (d, patch) => setGrid((g) => ({ ...g, [d]: { ...cell(d), ...patch } }));
  const loadExisting = () => {
    const g = {};
    days.forEach((d) => {
      const ex = existFor(d);
      if (ex) g[d] = { status: ex.status || "Present", in_time: ex.in_time || "", out_time: ex.out_time || "", _id: ex.id };
    });
    setGrid(g);
  };
  useEffect(() => { if (open && staffName) loadExisting(); else setGrid({}); }, [open, staffName, month, existing.length]); // eslint-disable-line
  const fillPresent = () => {
    const g = { ...grid };
    days.forEach((d) => {
      const sun = new Date(yy, mm - 1, d).getDay() === 0;
      if (!g[d]?.status) g[d] = { ...cell(d), status: sun ? "Holiday" : "Present" };
    });
    setGrid(g);
  };
  const filled = days.filter((d) => cell(d).status);
  const save = async () => {
    if (!staffName.trim()) { toast.error("Pick the staff name"); return; }
    if (!filled.length) { toast.error("Nothing to save — set at least one day"); return; }
    setBusy(true);
    let ok = 0, fail = 0;
    const desig = staff.find((s) => s.name === staffName)?.designation || "";
    for (const d of filled) {
      const c = cell(d);
      const body = { branch, date: dateOf(d), staff_name: staffName, designation: desig, status: c.status, in_time: c.in_time || "", out_time: c.out_time || "", remarks: "" };
      try {
        if (c._id) await api.put(`/attendance/${c._id}`, body);
        else await api.post("/attendance", body);
        ok += 1;
      } catch { fail += 1; }
    }
    setBusy(false);
    toast[fail ? "error" : "success"](`${ok} day${ok === 1 ? "" : "s"} saved for ${staffName}${fail ? `, ${fail} failed` : ""}`);
    api.get("/attendance").then((r) => setExisting(r.data || [])).catch(() => {});
  };
  const ST = [["P", "Present"], ["HD", "Half Day"], ["A", "Absent"], ["L", "Leave"], ["H", "Holiday"]];
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="month-entry-btn">
        <Grid3X3 className="mr-2 h-4 w-4" /> Month Entry
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Month Entry — type from the filled attendance card</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <select className="h-9 rounded-md border bg-background px-2 text-sm" value={branch} onChange={(e) => { setBranch(e.target.value); setStaffName(""); }} data-testid="month-entry-branch">
                {OFFICES.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
              <select className="h-9 min-w-[180px] rounded-md border bg-background px-2 text-sm" value={staffName} onChange={(e) => setStaffName(e.target.value)} data-testid="month-entry-staff">
                <option value="">— Staff name —</option>
                {staff.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
              </select>
              <Input type="month" className="w-[150px]" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="month-entry-month" />
              <Button size="sm" variant="outline" onClick={fillPresent} disabled={!staffName} data-testid="month-entry-fill-btn">Fill All P (Sun = H)</Button>
            </div>
            {!staffName ? (
              <div className="py-8 text-center text-sm text-muted-foreground">Pick the branch and staff name to start. Existing entries load automatically.</div>
            ) : (
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-2 py-1.5 text-left">Day</th>
                      <th className="px-2 py-1.5 text-left">Status</th>
                      <th className="px-2 py-1.5 text-left">In</th>
                      <th className="px-2 py-1.5 text-left">Out</th>
                    </tr>
                  </thead>
                  <tbody>
                    {days.map((d) => {
                      const dt = new Date(yy, mm - 1, d);
                      const sun = dt.getDay() === 0;
                      const c = cell(d);
                      return (
                        <tr key={d} className={`border-t ${sun ? "bg-red-50/60" : ""}`} data-testid="month-entry-row">
                          <td className={`whitespace-nowrap px-2 py-1 font-medium ${sun ? "text-red-600" : ""}`}>
                            {d} {dt.toLocaleDateString("en-GB", { weekday: "short" })}{c._id ? " •" : ""}
                          </td>
                          <td className="px-2 py-1">
                            <div className="flex gap-1">
                              {ST.map(([code, full]) => (
                                <button
                                  key={code}
                                  type="button"
                                  onClick={() => setCell(d, { status: c.status === full ? "" : full })}
                                  className={`h-6 w-7 rounded border text-[10px] font-bold ${c.status === full ? "border-emerald-600 bg-emerald-600 text-white" : "bg-white text-slate-600 hover:bg-slate-100"}`}
                                  data-testid={`month-entry-${code}-${d}`}
                                >
                                  {code}
                                </button>
                              ))}
                            </div>
                          </td>
                          <td className="px-2 py-1"><input type="time" className="h-6 rounded border px-1 text-[11px]" value={c.in_time} onChange={(e) => setCell(d, { in_time: e.target.value })} /></td>
                          <td className="px-2 py-1"><input type="time" className="h-6 rounded border px-1 text-[11px]" value={c.out_time} onChange={(e) => setCell(d, { out_time: e.target.value })} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {staffName && (
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{filled.length} of {daysIn} days set · "•" = already in register (will be updated)</span>
                <Button onClick={save} disabled={busy || !filled.length} data-testid="month-entry-save-btn">{busy ? "Saving…" : `Save ${filled.length} day${filled.length === 1 ? "" : "s"}`}</Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

const exportAttendanceCsv = (list) => {
  const headers = ["Sl No.", "Branch", "Date", "Staff Name", "Designation", "In Time", "Out Time", "Status", "Remarks"];
  const cell = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const lines = [headers.join(",")];
  list.forEach((r, i) => {
    lines.push([i + 1, r.branch || "Chennai", r.date ? fmtDate(r.date) : "", r.staff_name || "", r.designation || "", r.in_time || "", r.out_time || "", r.status || "Present", r.remarks || ""].map(cell).join(","));
  });
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `ntc-attendance-${today()}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export default function Attendance() {
  const [masters, setMasters] = useState([]);
  useEffect(() => {
    api.get("/staff-master").then((r) => setMasters((r.data || []).filter((m) => (m.status || "Active") !== "Inactive"))).catch(() => {});
  }, []);
  const staffField = masters.length
    ? {
        key: "staff_name", label: "Staff Name", type: "lookup", placeholder: "Select staff",
        options: masters.map((m) => ({ value: m.name, label: `${m.name} (${m.branch || "Chennai"})`, patch: { designation: m.designation || "", branch: m.branch || "Chennai" } })),
      }
    : { key: "staff_name", label: "Staff Name", type: "text", placeholder: "Full name", required: true };
  return (
    <EntityManager
      title="NTC Attendance"
      subtitle="Daily staff attendance register — branch-wise."
      endpoint="attendance"
      entityName="Attendance"
      selectable
      onPrintSelected={(list) => printAttendanceRegister(list)}
      exportCsv={exportAttendanceCsv}
      headerExtra={(rows, displayRows, filterVals) => (
        <>
          <Button
            variant="outline"
            onClick={async () => {
              const def = new Date().toISOString().slice(0, 7);
              const m = window.prompt("Month for the card (YYYY-MM) — days, Sundays & holidays will be printed.\nLeave blank for a fully blank card:", def);
              if (m === null) return;
              const selBranch = filterVals?.branch && filterVals.branch !== "All" ? filterVals.branch : "";
              try {
                const hres = await api.get("/calendar-events");
                const hols = (hres.data || []).filter((e) => e.type === "Holiday");
                printBlankAttendanceCard((m || "").trim(), selBranch, hols);
              } catch { printBlankAttendanceCard((m || "").trim(), selBranch, []); }
            }}
            data-testid="blank-attendance-card-btn"
          >
            <Printer className="mr-2 h-4 w-4" /> Blank Card
          </Button>
          <Button
            variant="outline"
            onClick={async () => {
              const def = new Date().toISOString().slice(0, 7);
              const m = window.prompt("Month for the cards (YYYY-MM):", def);
              if (m === null || !/^\d{4}-\d{2}$/.test((m || "").trim())) { if (m !== null) toast.error("Enter month as YYYY-MM"); return; }
              const branch = window.prompt(`Branch (${OFFICES.join(" / ")}) — leave blank for ALL branches:`, "");
              if (branch === null) return;
              try {
                const res = await api.get("/staff-master");
                const staff = (res.data || [])
                  .filter((s) => (s.status || "Active") !== "Inactive")
                  .filter((s) => !branch.trim() || (s.branch || "Chennai").toLowerCase() === branch.trim().toLowerCase())
                  .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
                if (!staff.length) { toast.error(`No active staff found${branch.trim() ? ` for ${branch.trim()}` : ""}`); return; }
                let hols = [];
                try { hols = ((await api.get("/calendar-events")).data || []).filter((e) => e.type === "Holiday"); } catch {}
                printBulkAttendanceCards(staff, m.trim(), hols);
                toast.success(`${staff.length} attendance card${staff.length > 1 ? "s" : ""} prepared — one per page`);
              } catch (e) { toast.error("Could not load staff list"); }
            }}
            data-testid="bulk-attendance-cards-btn"
          >
            <Printer className="mr-2 h-4 w-4" /> Bulk Cards
          </Button>
          <MonthlySummary />
          <MonthEntryGrid />
          <HolidayManager />
        </>
      )}
      formExtra={(form, setForm) => <HolidayNotice form={form} setForm={setForm} />}
      formExtraAfter="status"
      defaults={{ branch: "Chennai", date: today(), staff_name: "", designation: "", in_time: "", out_time: "", status: "Present", remarks: "" }}
      fields={[
        { key: "branch", label: "Branch", type: "select", options: OFFICES },
        { key: "date", label: "Date", type: "date" },
        staffField,
        { key: "designation", label: "Designation", type: "text", placeholder: "e.g. Traffic In Charge" },
        { key: "in_time", label: "In Time", type: "time" },
        { key: "out_time", label: "Out Time", type: "time" },
        { key: "status", label: "Status", type: "select", options: STATUS },
        { key: "remarks", label: "Remarks", type: "textarea", placeholder: "Optional" },
      ]}
      filters={[
        { key: "branch", label: "Branch", type: "select", options: ["All", ...OFFICES], match: (r, v) => !v || v === "All" || (r.branch || "Chennai") === v },
        { key: "fdate", label: "Date", type: "date", match: (r, v) => !v || r.date === v },
      ]}
      columns={[
        { key: "sl", label: "Sl No.", render: (r, i) => i + 1 },
        { key: "branch", label: "Branch" },
        { key: "date", label: "Date", render: (r) => (r.date ? fmtDate(r.date) : "—") },
        { key: "staff_name", label: "Staff Name" },
        { key: "designation", label: "Designation", render: (r) => r.designation || "—" },
        { key: "in_time", label: "In Time", render: (r) => r.in_time || "—" },
        { key: "out_time", label: "Out Time", render: (r) => r.out_time || "—" },
        { key: "status", label: "Status", render: (r) => <Badge variant="outline" className={tone(r.status)}>{r.status || "Present"}</Badge> },
        { key: "remarks", label: "Remarks", render: (r) => (r.remarks ? <span className="block max-w-[220px] truncate text-xs text-slate-600" title={r.remarks} data-testid="attendance-remarks-cell">{r.remarks}</span> : "—") },
      ]}
    />
  );
}
